import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { getProducts, createProduct, updateProduct, deleteProduct } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Package, X } from 'lucide-react';

const Products = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: 0,
    cost: 0,
    category: '',
    product_type: 'PRODUCTO',
    track_inventory: true,
    gender: 'Mujer',
    sizes: [],
    colors: [],
    min_stock: 5
  });
  
  // Para agregar tallas y colores
  const [newSize, setNewSize] = useState('');
  const [newColor, setNewColor] = useState('');

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const data = await getProducts();
      setProducts(data);
    } catch (error) {
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.price || !formData.category) {
      toast.error('Nombre, precio y categoría son requeridos');
      return;
    }
    if (formData.product_type === 'PRODUCTO' && !formData.gender) {
      toast.error('Género es requerido para productos físicos');
      return;
    }
    
    try {
      const dataToSave = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        cost: parseFloat(formData.cost) || 0,
        min_stock: parseInt(formData.min_stock) || 5,
        product_type: formData.product_type || 'PRODUCTO',
        track_inventory: formData.product_type !== 'SERVICIO',
        gender: formData.product_type === 'SERVICIO' ? '' : formData.gender,
        sizes: formData.product_type === 'SERVICIO' ? [] : formData.sizes,
        colors: formData.product_type === 'SERVICIO' ? [] : formData.colors,
      };
      
      if (editingProduct) {
        await updateProduct(editingProduct.id, dataToSave);
        toast.success('Producto actualizado');
      } else {
        await createProduct(dataToSave);
        toast.success('Producto creado');
      }
      setIsDialogOpen(false);
      resetForm();
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar producto');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      description: product.description || '',
      price: product.price || 0,
      cost: product.cost || 0,
      category: product.category || '',
      product_type: product.product_type || 'PRODUCTO',
      track_inventory: product.track_inventory !== false,
      gender: product.product_type === 'SERVICIO' ? '' : (product.gender || 'Mujer'),
      sizes: product.sizes || [],
      colors: product.colors || [],
      min_stock: product.min_stock || 5
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este producto?')) {
      try {
        await deleteProduct(id);
        toast.success('Producto eliminado');
        loadProducts();
      } catch (error) {
        toast.error('Error al eliminar producto');
      }
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      price: 0,
      cost: 0,
      category: '',
      gender: 'Mujer',
      sizes: [],
      colors: [],
      min_stock: 5
    });
    setNewSize('');
    setNewColor('');
  };

  const addSize = () => {
    if (newSize && !formData.sizes.includes(newSize)) {
      setFormData({ ...formData, sizes: [...formData.sizes, newSize] });
      setNewSize('');
    }
  };

  const removeSize = (size) => {
    setFormData({ ...formData, sizes: formData.sizes.filter(s => s !== size) });
  };

  const addColor = () => {
    if (newColor && !formData.colors.includes(newColor)) {
      setFormData({ ...formData, colors: [...formData.colors, newColor] });
      setNewColor('');
    }
  };

  const removeColor = (color) => {
    setFormData({ ...formData, colors: formData.colors.filter(c => c !== color) });
  };

  // Tallas predefinidas comunes
  const commonSizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '2', '4', '6', '8', '10', '12', '14', '16'];
  // Colores predefinidos comunes
  const commonColors = ['Blanco', 'Negro', 'Azul', 'Rojo', 'Verde', 'Rosa', 'Gris', 'Beige', 'Celeste', 'Morado'];

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Cargando...</div>;
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="products-page">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Outfit' }}>Productos</h1>
        {user?.role === 'admin' && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={resetForm}
                className="bg-cyan-500 hover:bg-cyan-600 text-white shadow-md"
                data-testid="add-product-button"
              >
                <Plus className="w-5 h-5 mr-2" />
                Nuevo Producto
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Outfit' }}>
                  {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label>Nombre del Producto *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ej: Bata Médica Clásica"
                      required
                      data-testid="product-name-input"
                    />
                  </div>

                  <div className="space-y-2 col-span-2">
                    <Label>Descripción</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Descripción del producto"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Precio de Venta *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      required
                      data-testid="product-price-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Costo</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.cost}
                      onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Categoría *</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger data-testid="product-category-select">
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Batas">Batas</SelectItem>
                        <SelectItem value="Scrubs">Scrubs</SelectItem>
                        <SelectItem value="Chacabanas">Chacabanas</SelectItem>
                        <SelectItem value="Accesorios">Accesorios</SelectItem>
                        <SelectItem value="Otros">Otros</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Tipo *</Label>
                    <Select
                      value={formData.product_type}
                      onValueChange={(value) => setFormData({
                        ...formData,
                        product_type: value,
                        track_inventory: value === 'PRODUCTO',
                        gender: value === 'SERVICIO' ? '' : (formData.gender || 'Mujer'),
                        sizes: value === 'SERVICIO' ? [] : formData.sizes,
                        colors: value === 'SERVICIO' ? [] : formData.colors
                      })}
                    >
                      <SelectTrigger data-testid="product-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PRODUCTO">Producto físico</SelectItem>
                        <SelectItem value="SERVICIO">Servicio</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.product_type === 'PRODUCTO' && (
                    <div className="space-y-2">
                      <Label>Género *</Label>
                      <Select
                        value={formData.gender}
                        onValueChange={(value) => setFormData({ ...formData, gender: value })}
                      >
                        <SelectTrigger data-testid="product-gender-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Mujer">Mujer</SelectItem>
                          <SelectItem value="Hombre">Hombre</SelectItem>
                          <SelectItem value="Unisex">Unisex</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {formData.product_type === 'PRODUCTO' && (
                    <div className="space-y-2">
                      <Label>Stock Mínimo (para alertas)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.min_stock}
                        onChange={(e) => setFormData({ ...formData, min_stock: e.target.value })}
                      />
                    </div>
                  )}
                </div>

                {formData.product_type === 'PRODUCTO' && (
                  <>
                {/* Tallas */}
                <div className="space-y-2 border-t pt-4">
                  <Label>Tallas Disponibles</Label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.sizes.map((size) => (
                      <Badge key={size} variant="secondary" className="flex items-center gap-1">
                        {size}
                        <button type="button" onClick={() => removeSize(size)} className="ml-1 hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                    {formData.sizes.length === 0 && (
                      <span className="text-sm text-slate-400">Sin tallas definidas</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Select value={newSize} onValueChange={setNewSize}>
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Seleccionar talla" />
                      </SelectTrigger>
                      <SelectContent>
                        {commonSizes.filter(s => !formData.sizes.includes(s)).map((size) => (
                          <SelectItem key={size} value={size}>{size}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button type="button" onClick={addSize} variant="outline" disabled={!newSize}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Colores */}
                <div className="space-y-2 border-t pt-4">
                  <Label>Colores Disponibles</Label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.colors.map((color) => (
                      <Badge key={color} variant="secondary" className="flex items-center gap-1">
                        <span 
                          className="w-3 h-3 rounded-full border mr-1"
                          style={{ backgroundColor: color.toLowerCase() }}
                        />
                        {color}
                        <button type="button" onClick={() => removeColor(color)} className="ml-1 hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                    {formData.colors.length === 0 && (
                      <span className="text-sm text-slate-400">Sin colores definidos</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Select value={newColor} onValueChange={setNewColor}>
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Seleccionar color" />
                      </SelectTrigger>
                      <SelectContent>
                        {commonColors.filter(c => !formData.colors.includes(c)).map((color) => (
                          <SelectItem key={color} value={color}>{color}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button type="button" onClick={addColor} variant="outline" disabled={!newColor}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                  </>
                )}

                {formData.product_type === 'PRODUCTO' ? (
                  <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-sm text-blue-800">
                    <strong>Nota:</strong> El stock se maneja en <strong>Inventario por Sucursal</strong>.
                    Aquí solo defines las tallas y colores disponibles para este producto.
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-sm text-amber-800">
                    <strong>Nota:</strong> Los servicios no manejan género, tallas, colores ni inventario por sucursal.
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1 bg-cyan-500 hover:bg-cyan-600" data-testid="save-product-button">
                    {editingProduct ? 'Actualizar' : 'Crear'} Producto
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancelar
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Lista de Productos */}
      {products.length === 0 ? (
        <Card className="bg-white border border-slate-200">
          <CardContent className="p-8 text-center">
            <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No hay productos registrados</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-grid">
          {products.map(product => (
            <Card 
              key={product.id} 
              className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`product-card-${product.id}`}
            >
              <CardHeader className="pb-3">
                <CardTitle className="flex items-start justify-between" style={{ fontFamily: 'Outfit' }}>
                  <div>
                    <h3 className="text-lg">{product.name}</h3>
                    <p className="text-sm text-slate-500 font-normal">
                      {product.category}
                      {product.product_type === 'SERVICIO' ? ' - Servicio' : ` - ${product.gender || 'Sin género'}`}
                    </p>
                  </div>
                  <Package className="w-5 h-5 text-cyan-500" />
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Badge variant={product.product_type === 'SERVICIO' ? 'secondary' : 'outline'}>
                  {product.product_type === 'SERVICIO' ? 'Servicio / Sin inventario' : 'Producto físico'}
                </Badge>

                {product.description && (
                  <p className="text-sm text-slate-600">{product.description}</p>
                )}
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Precio:</span>
                  <span className="text-xl font-bold text-cyan-600" style={{ fontFamily: 'JetBrains Mono' }}>
                    ${(product.price || 0).toFixed(2)}
                  </span>
                </div>

                {product.cost > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-500">Costo:</span>
                    <span className="text-slate-600">${(product.cost || 0).toFixed(2)}</span>
                  </div>
                )}

                {/* Tallas */}
                {product.product_type !== 'SERVICIO' && product.sizes && product.sizes.length > 0 && (
                  <div>
                    <span className="text-xs text-slate-500">Tallas:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {product.sizes.map((size) => (
                        <Badge key={size} variant="outline" className="text-xs">
                          {size}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Colores */}
                {product.product_type !== 'SERVICIO' && product.colors && product.colors.length > 0 && (
                  <div>
                    <span className="text-xs text-slate-500">Colores:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {product.colors.map((color) => (
                        <Badge key={color} variant="outline" className="text-xs flex items-center gap-1">
                          <span 
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: color.toLowerCase() }}
                          />
                          {color}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {user?.role === 'admin' && (
                  <div className="flex gap-2 pt-3 border-t">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => handleEdit(product)}
                      data-testid={`edit-product-${product.id}`}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      className="text-red-500 hover:bg-red-50"
                      onClick={() => handleDelete(product.id)}
                      data-testid={`delete-product-${product.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Products;